import { useState, useCallback } from 'react'
import { useViewsQuery } from '@/hooks/useDatabase'
import type { DatabaseConnection } from '@/stores/connectionStore'
import type { View } from '@/types/compatibility'
import {
  EyeIcon,
  MagnifyingGlassIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline'

interface ViewsViewProps {
  connection: DatabaseConnection
  database: string
  schema?: string
  onViewSelect?: (viewName: string) => void
  onViewDefinition?: (viewName: string) => void
}

export function ViewsView({ connection, database, schema, onViewSelect, onViewDefinition }: ViewsViewProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const { data: viewsResponse, isLoading, error } = useViewsQuery(
    connection.id,
    database,
    schema,
    { enabled: true, databaseType: connection.type === 'mysql' ? 'MySQL' : 'PostgreSQL' }
  )

  const allViews = Array.isArray(viewsResponse) ? viewsResponse : []

  // Filter and sort views
  const filteredAndSortedViews = allViews
    .filter((view: View) => {
      if (!searchTerm) return true
      return view.name?.toLowerCase().includes(searchTerm.toLowerCase())
    })
    .sort((a: View, b: View) => {
      const aValue = a.name || ''
      const bValue = b.name || ''
      const comparison = aValue.localeCompare(bValue)
      return sortOrder === 'asc' ? comparison : -comparison
    })

  const handleSort = useCallback((_field: 'name') => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
  }, [sortOrder])

  const handleSortName = useCallback(() => { handleSort('name') }, [handleSort])
  
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])

  const getSortIcon = (_field: 'name') => {
    return sortOrder === 'asc' ? 
      <div className="w-4 h-4 text-blue-600">↑</div> : 
      <div className="w-4 h-4 text-blue-600">↓</div>
  }

  const getViewIcon = (_view: View) => {
    return <EyeIcon className="w-4 h-4 text-blue-600" />
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Error loading views: {error.message || 'Unknown error'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <EyeIcon className="w-6 h-6 mr-2 text-blue-600" />
              Views
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {database}{schema ? `.${schema}` : ''} • {filteredAndSortedViews.length} views
            </p>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search views by name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      {/* Views Table */}
      <div className="flex-1 overflow-auto">
        <div className="min-w-full">
          {/* Table Header */}
          <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 sticky top-0 z-10">
            <div className="grid grid-cols-10 gap-4 px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <div 
                className="col-span-8 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                onClick={handleSortName}
              >
                View Name
                {getSortIcon('name')}
              </div>
              <div className="col-span-2">Actions</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200 dark:divide-gray-600">
            {filteredAndSortedViews.length === 0 ? (
              <div className="text-center py-12">
                <EyeIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  {searchTerm ? 'No views match your criteria' : 'No views found'}
                </p>
              </div>
            ) : (
              filteredAndSortedViews.map((view: View) => (
                <div key={view.name} className="grid grid-cols-10 gap-4 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <div 
                    className="col-span-8 flex items-center cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                    onClick={() => { onViewSelect?.(view.name!) }}
                    title="Click to view data"
                  >
                    {getViewIcon(view)}
                    <div className="ml-2">
                      <span className="font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400">
                        {view.name}
                      </span>
                    </div>
                  </div>

                  <div className="col-span-2 flex items-center space-x-2">
                    <button type="button"
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                      title="View properties and definition"
                      onClick={() => { onViewDefinition?.(view.name!) }}
                    >
                      <CodeBracketIcon className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Footer Stats */}
      <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-700">
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div>
            Showing {filteredAndSortedViews.length} of {allViews.length} views
          </div>
        </div>
      </div>
    </div>
  )
}
